/** Gravity Nudge mutation — shifts a random column section left or right by 1 */

import type { PRNG } from '../../rng.js';
import type { BoardState, MutationCard, Bubble } from '../../types.js';
import { coordToKey, isValidCoord } from '../../grid/hex.js';

export function applyGravityNudge(
  board: BoardState,
  _card: MutationCard,
  rng: PRNG,
): BoardState {
  if (board.bubbles.size === 0) return board;

  // Pick a random row range to shift
  const rows = new Set<number>();
  for (const [, bubble] of board.bubbles) {
    rows.add(bubble.coord.r);
  }
  if (rows.size === 0) return board;

  const rowArray = [...rows];
  const targetRow = rng.pick(rowArray);

  // Random direction: -1 (left) or +1 (right)
  const direction = rng.next() < 0.5 ? -1 : 1;

  const newBubbles = new Map<string, Bubble>();

  for (const [, bubble] of board.bubbles) {
    if (bubble.coord.r === targetRow) {
      const newQ = bubble.coord.q + direction;
      const newCoord = { q: newQ, r: bubble.coord.r };

      // Only move if new position is valid and not already occupied by a non-shifted bubble.
      // Pass board.ceilingRow as minRow so buffer/descended rows (r < 0) are
      // accepted — without this, every bubble in any row above the ceiling was
      // silently dropped, severing the board.
      if (isValidCoord(newCoord, board.width, board.ceilingRow) && newQ >= 0) {
        const shifted: Bubble = { ...bubble, coord: newCoord };
        newBubbles.set(coordToKey(newCoord), shifted);
      }
      // If invalid, bubble is lost (pushed off the q boundary)
    } else {
      newBubbles.set(coordToKey(bubble.coord), bubble);
    }
  }

  return { ...board, bubbles: newBubbles };
}
